import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  textprintMachinesApi, textprintCommandesApi, textprintOrdresApi, textprintConsommablesApi,
  type AjustementStockPayload,
  type CommandeListParams, type CreateCommandePayload, type UpdateCommandePayload,
  type ConsommableListParams, type CreateConsommablePayload, type UpdateConsommablePayload,
  type CreateMachinePayload, type MachineListParams, type UpdateMachinePayload,
  type CreateOrdrePayload, type OrdreListParams, type UpdateOrdrePayload,
} from '@/api/textprint';

const MACHINES_KEY     = ['operations', 'textprint', 'machines']     as const;
const COMMANDES_KEY    = ['operations', 'textprint', 'commandes']    as const;
const ORDRES_KEY       = ['operations', 'textprint', 'ordres']       as const;
const CONSOMMABLES_KEY = ['operations', 'textprint', 'consommables'] as const;

// ── Machines ───────────────────────────────────────────────────────────────

export const useMachines = (params: MachineListParams = {}) =>
  useQuery({ queryKey: [...MACHINES_KEY, params], queryFn: () => textprintMachinesApi.list(params), staleTime: 30_000 });

export const useMachine = (id: string | undefined) =>
  useQuery({ queryKey: [...MACHINES_KEY, id], queryFn: () => textprintMachinesApi.get(id!), enabled: !!id, staleTime: 15_000 });

export const useCreateMachine = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CreateMachinePayload) => textprintMachinesApi.create(p),
    onSuccess:  () => qc.invalidateQueries({ queryKey: MACHINES_KEY }),
  });
};

export const useUpdateMachine = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateMachinePayload }) => textprintMachinesApi.update(id, payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: MACHINES_KEY }),
  });
};

export const useDeleteMachine = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => textprintMachinesApi.remove(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: MACHINES_KEY }),
  });
};

// ── Commandes ──────────────────────────────────────────────────────────────

export const useCommandesImpression = (params: CommandeListParams = {}) =>
  useQuery({ queryKey: [...COMMANDES_KEY, params], queryFn: () => textprintCommandesApi.list(params), staleTime: 30_000 });

export const useCommandeImpression = (id: string | undefined) =>
  useQuery({ queryKey: [...COMMANDES_KEY, id], queryFn: () => textprintCommandesApi.get(id!), enabled: !!id, staleTime: 15_000 });

export const useCreateCommandeImpression = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CreateCommandePayload) => textprintCommandesApi.create(p),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: COMMANDES_KEY });
      qc.invalidateQueries({ queryKey: ORDRES_KEY });
    },
  });
};

export const useUpdateCommandeImpression = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCommandePayload }) => textprintCommandesApi.update(id, payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: COMMANDES_KEY }),
  });
};

export const useDeleteCommandeImpression = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => textprintCommandesApi.remove(id),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: COMMANDES_KEY });
      qc.invalidateQueries({ queryKey: ORDRES_KEY });
    },
  });
};

// ── OF Impression ──────────────────────────────────────────────────────────

export const useOrdresImpression = (params: OrdreListParams = {}) =>
  useQuery({ queryKey: [...ORDRES_KEY, params], queryFn: () => textprintOrdresApi.list(params), staleTime: 30_000 });

export const useOrdreImpression = (id: string | undefined) =>
  useQuery({ queryKey: [...ORDRES_KEY, id], queryFn: () => textprintOrdresApi.get(id!), enabled: !!id, staleTime: 15_000 });

export const useCreateOrdreImpression = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CreateOrdrePayload) => textprintOrdresApi.create(p),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ORDRES_KEY }),
  });
};

export const useUpdateOrdreImpression = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateOrdrePayload }) => textprintOrdresApi.update(id, payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ORDRES_KEY }),
  });
};

export const useDeleteOrdreImpression = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => textprintOrdresApi.remove(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ORDRES_KEY }),
  });
};

// ── Consommables ───────────────────────────────────────────────────────────

export const useConsommables = (params: ConsommableListParams = {}) =>
  useQuery({ queryKey: [...CONSOMMABLES_KEY, params], queryFn: () => textprintConsommablesApi.list(params), staleTime: 30_000 });

export const useConsommable = (id: string | undefined) =>
  useQuery({ queryKey: [...CONSOMMABLES_KEY, id], queryFn: () => textprintConsommablesApi.get(id!), enabled: !!id, staleTime: 15_000 });

export const useCreateConsommable = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CreateConsommablePayload) => textprintConsommablesApi.create(p),
    onSuccess:  () => qc.invalidateQueries({ queryKey: CONSOMMABLES_KEY }),
  });
};

export const useUpdateConsommable = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateConsommablePayload }) => textprintConsommablesApi.update(id, payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: CONSOMMABLES_KEY }),
  });
};

export const useAjusterStock = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: AjustementStockPayload }) => textprintConsommablesApi.ajusterStock(id, payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: CONSOMMABLES_KEY }),
  });
};

export const useDeleteConsommable = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => textprintConsommablesApi.remove(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: CONSOMMABLES_KEY }),
  });
};
